import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { startGoogleWebOAuth } from "@/utils/googleWebAuth";
import { ResponsiveModal, ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface MetefyQrAuthDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export function MetefyQrAuthDrawer({ isOpen, setIsOpen }: MetefyQrAuthDrawerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const googleClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);

      // Store source in localStorage for redirect handling (same as Auth page)
      localStorage.setItem('metefy_oauth_source', 'auth');

      if (Capacitor.isNativePlatform()) {
        console.log('[MetefyQrAuthDrawer] Starting native Google Sign-In...');

        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');

        if (!googleClientId) {
          throw new Error('Google client ID is not configured');
        }

        try {
          await GoogleAuth.initialize({
            clientId: googleClientId,
            scopes: ['profile', 'email'],
            grantOfflineAccess: true
          });
        } catch (initError) {
          console.log('[MetefyQrAuthDrawer] GoogleAuth already initialized or init error:', initError);
        }

        const googleUser = await GoogleAuth.signIn();
        console.log('[MetefyQrAuthDrawer] Google Sign-In successful, got user:', googleUser.email);

        const idToken = googleUser.authentication.idToken;
        if (!idToken) {
          throw new Error('No ID token received from Google');
        }

        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken,
        });

        if (error) {
          console.error('[MetefyQrAuthDrawer] Supabase signInWithIdToken error:', error);
          throw error;
        }

        if (data.session) {
          // Successfully signed in - navigate to profile like Auth page
          navigate('/profile');
          setIsOpen(false);
        }
      } else {
        // WEB: Use custom Google OAuth flow that returns an ID token
        if (!googleClientId) {
          throw new Error('Google client ID is not configured');
        }

        startGoogleWebOAuth();
      }
    } catch (error: any) {
      console.error('[MetefyQrAuthDrawer] Google Sign-In Error:', error);
      localStorage.removeItem('metefy_oauth_source');
      toast({
        title: "Error signing in with Google",
        description: error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle browser back button: close drawer instead of navigating away
  useEffect(() => {
    if (!isOpen) return;

    window.history.pushState({ metefyQrAuthDrawerOpen: true }, '');

    const onPopState = () => {
      // Close the drawer if it's open
      setIsOpen(false);
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isOpen, setIsOpen]);

  return (
    <ResponsiveModal open={isOpen} setOpen={setIsOpen} direction="bottom" drawerClassName="z-[60]" classname="max-w-md" alwaysCenter={true}>
      <ResponsiveModalContent className="w-full max-w-md mx-auto bg-card border-none px-0 py-8">
        <div className="space-y-3 relative px-4 sm:px-6 bg-card">
          <div className="text-center mb-1">
            <div 
              className="w-14 h-14 mx-auto mb-2"
              style={{
                maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: 'hsl(var(--primary))',
                WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
              role="img"
              aria-label="Metefy Logo"
            />
            <h2 className="text-xl font-semibold text-foreground">
              Sign in to Metefy
            </h2>
            <p className="text-sm text-foreground/70 mt-1">
              For the smoothest experience, use Google Sign In below.
            </p>
          </div>

          <div className="mt-4">
            <Button
              onClick={handleGoogleSignIn}
              variant="outline"
              className="w-full mb-3 border-2 border-primary text-foreground font-semibold text-base shadow whitespace-nowrap hover:bg-primary hover:text-primary-foreground transition-colors px-8 py-4"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 21 20" fill="none">
                    <g clipPath="url(#clip0_13183_10121)">
                      <path d="M20.3081 10.2303C20.3081 9.55056 20.253 8.86711 20.1354 8.19814H10.7031V12.0492H16.1046C15.8804 13.2911 15.1602 14.3898 14.1057 15.0879V17.5866H17.3282C19.2205 15.8449 20.3081 13.2728 20.3081 10.2303Z" fill="#3F83F8"></path>
                      <path d="M10.7019 20.0006C13.3989 20.0006 15.6734 19.1151 17.3306 17.5865L14.1081 15.0879C13.2115 15.6979 12.0541 16.0433 10.7056 16.0433C8.09669 16.0433 5.88468 14.2832 5.091 11.9169H1.76562V14.4927C3.46322 17.8695 6.92087 20.0006 10.7019 20.0006V20.0006Z" fill="#34A853"></path>
                      <path d="M5.08857 11.9169C4.66969 10.6749 4.66969 9.33008 5.08857 8.08811V5.51233H1.76688C0.348541 8.33798 0.348541 11.667 1.76688 14.4927L5.08857 11.9169V11.9169Z" fill="#FBBC04"></path>
                      <path d="M10.7019 3.95805C12.1276 3.936 13.5055 4.47247 14.538 5.45722L17.393 2.60218C15.5852 0.904587 13.1858 -0.0287217 10.7019 0.000673888C6.92087 0.000673888 3.46322 2.13185 1.76562 5.51234L5.08732 8.08813C5.87733 5.71811 8.09302 3.95805 10.7019 3.95805V3.95805Z" fill="#EA4335"></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_13183_10121">
                        <rect width="20" height="20" fill="white" transform="translate(0.5)"></rect>
                      </clipPath>
                    </defs>
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>
            <p className="text-xs text-foreground/70 text-center">
              By continuing, you agree to Metefy's{" "}
              <Link to="/terms-and-conditions" className="text-primary hover:underline">
                Terms of Service
              </Link>{" "}
              <span className="text-foreground/70">and</span>{" "}
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>.
            </p>
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

